import { IconPencil, IconPlus } from '@tabler/icons-react'
import { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { AstraBadge, AstraButton, AstraIcon, AstraParagraph, AstraTooltip, astraTwMerge, ColumnSize, DataTable, RowType } from '~/astra'
import { PractitionerChipWithHover } from '~/components/PractitionerChipWithHover'
import { DipsResource, type HospitalConfigPractitioner } from '~/storm'
import { useSpeciality } from '~/storm'
import { getSpecialityName } from '~/storm/common/specialty'
import { getPractitionerFullname } from '~/utils/dips'

import { useResourceNavigation } from '../useResourceNavigation'

type Column<T extends string = string> = ColumnSize & {
    id: T
    label: string
}

export type SurgeonStatus = 'active' | 'inactive' | 'new'

export type PractitionerTableViewModel = HospitalConfigPractitioner & {
    status: SurgeonStatus
}

export type DipsSurgeonTableViewModel = DipsResource & {
    status: SurgeonStatus
}

const TextCell = ({ children }: { children: ReactNode }) => (
    <AstraParagraph variant="body" className="truncate whitespace-normal text-default">
        {children}
    </AstraParagraph>
)

const leftPadding = 'pl-6'
const bodyCellClassName = twMerge('flex h-full w-full !cursor-default !select-text items-center border-none', leftPadding)

const getHeaders = <T extends Column>(_cols: T[]): RowType<T>[] => [
    {
        key: 'header',
        cellClassName: () => twMerge('flex w-full bg-surface-strong p-3', leftPadding),
        cellRender: ({ label }) => (
            <AstraParagraph weight="medium" variant="body-small" className="text-default">
                {label}
            </AstraParagraph>
        ),
    },
]

export const StatusBadge = ({ status }: { status: SurgeonStatus | undefined | null }) => {
    if (status === 'new') return <AstraBadge text="Ny" color="magic" size="small" />
    if (status === 'active') return <AstraBadge text="Aktiv" color="success" size="small" />
    if (status === 'inactive') return <AstraBadge text="Inaktiv" color="critical" size="small" />
    return null
}

const nameColumns = [
    { id: 'shortName', label: 'Navn' },
    { id: 'fullName', label: 'Fullt navn' },
] as const satisfies Column[]

const activeSurgeonCols = [
    ...nameColumns,
    { id: 'speciality', label: 'Fagområde' },
    { id: 'status', label: 'Status' },
    { id: 'action', label: '' },
] as const satisfies Column[]
type ActiveSurgeonColumn = (typeof activeSurgeonCols)[number]

const PractitionerCell = ({
    column,
    practitioner,
    onEdit,
}: {
    column: ActiveSurgeonColumn
    practitioner: PractitionerTableViewModel
    onEdit: (practitioner: HospitalConfigPractitioner) => void
}) => {
    const speciality = useSpeciality({ where: practitioner.speciality_id ? { id: practitioner.speciality_id } : { id: -1 } })
    if (column.id === 'speciality') {
        const specialitiesString = getSpecialityName(speciality)
        return (
            <AstraTooltip tooltipContent={specialitiesString}>
                <TextCell>{specialitiesString}</TextCell>
            </AstraTooltip>
        )
    }
    if (column.id === 'shortName') {
        return <PractitionerChipWithHover practitioner={practitioner} />
    }
    if (column.id === 'fullName') {
        return <TextCell>{getPractitionerFullname(practitioner)}</TextCell>
    }
    if (column.id === 'status') {
        return <StatusBadge status={practitioner.status} />
    }
    column.id satisfies 'action'
    return (
        <AstraButton variant="ghost" size="medium" label="Rediger" icon={<AstraIcon icon={IconPencil} size="small" />} onClick={() => onEdit(practitioner)} />
    )
}

const EmptyTable = ({ headerColumns, message = 'Fant ingen kirurger' }: { headerColumns: Column[]; message?: string }) => (
    <DataTable
        cols={headerColumns}
        rows={[{ id: 'empty' }]}
        fullWidthCells
        headers={getHeaders(headerColumns)}
        className="border-spacing-0"
        optimizeRendering={{ cellHeight: 'tall' }}
        body={{
            cellClassName: (_, col) =>
                astraTwMerge(bodyCellClassName, col.id === headerColumns[0]?.id ? 'col-span-full flex items-center justify-center border-none' : 'hidden'),
            cellRender: (_, column) =>
                column.id === headerColumns[0]?.id ? (
                    <AstraParagraph variant="body" className="text-default">
                        {message}
                    </AstraParagraph>
                ) : null,
        }}
    />
)

export const PractitionersTable = ({ practitioners }: { practitioners: PractitionerTableViewModel[] }) => {
    const { navigateToEdit } = useResourceNavigation()

    if (practitioners.length === 0) {
        return <EmptyTable headerColumns={activeSurgeonCols} />
    }
    return (
        <DataTable
            cols={activeSurgeonCols}
            rows={practitioners}
            fullWidthCells
            headers={getHeaders(activeSurgeonCols)}
            optimizeRendering={{ cellHeight: 'tall' }}
            className="border-spacing-0"
            body={{
                cellClassName: () => bodyCellClassName,
                cellRender: (practitioner, column) => <PractitionerCell column={column} practitioner={practitioner} onEdit={p => navigateToEdit(p.id)} />,
            }}
        />
    )
}

const dipsCols = [...nameColumns, { id: 'status', label: 'Status' }, { id: 'action', label: '' }] as const satisfies Column[]

type DipsColKey = (typeof dipsCols)[number]['id']
type DipsSurgeonColumn = (typeof dipsCols)[number]

const DipsSurgeonCell = ({
    column,
    resource,
    onAdd,
}: {
    column: DipsSurgeonColumn
    resource: DipsSurgeonTableViewModel
    onAdd: (resource: DipsSurgeonTableViewModel) => void
}) => {
    const cell: Record<DipsColKey, ReactNode> = {
        shortName: <TextCell>{resource.shortName ?? '–'}</TextCell>,
        fullName: <TextCell>{resource.name ?? '–'}</TextCell>,
        status: <StatusBadge status={resource.status} />,
        action: resource.status === 'new' && (
            <AstraButton
                variant="primary"
                size="medium"
                label="Legg til Hero"
                icon={<AstraIcon icon={IconPlus} size="small" />}
                onClick={() => onAdd(resource)}
            />
        ),
    }

    return cell[column.id]
}

export const DipsSurgeonsTable = ({ resources }: { resources: DipsSurgeonTableViewModel[] }) => {
    const { navigateToNew } = useResourceNavigation()

    if (resources.length === 0) {
        return <EmptyTable headerColumns={dipsCols} message="Fant ingen kirurger med oppdateringer fra DIPS" />
    }
    return (
        <DataTable
            cols={dipsCols}
            rows={resources}
            fullWidthCells
            headers={getHeaders(dipsCols)}
            optimizeRendering={{ cellHeight: 'tall' }}
            className="border-spacing-0"
            body={{
                cellClassName: () => bodyCellClassName,
                cellRender: (resource, column) => <DipsSurgeonCell column={column} resource={resource} onAdd={r => navigateToNew(r.id)} />,
            }}
        />
    )
}
